import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import './AuditLog.css';

interface AuditEntry {
  id: number;
  event_type: string;
  user_id: number | null;
  user_email: string | null;
  ip_address: string | null;
  user_agent: string | null;
  details: Record<string, any> | null;
  resource_type: string | null;
  resource_id: string | null;
  created_at: string;
}

interface AuditResponse {
  logs: AuditEntry[];
  total: number;
  page: number;
  per_page: number;
  pages: number;
}

const EVENT_TYPES = [
  'LOGIN_SUCCESS', 'LOGIN_FAILED', 'LOGOUT', 'PASSWORD_CHANGE',
  'USER_CREATED', 'USER_DELETED', 'ROLE_CHANGE', 'DATA_EXPORT',
  'OAUTH_CONNECT', 'OAUTH_DISCONNECT', 'TOKEN_REFRESH', 'UPLOAD',
  'SETTINGS_CHANGE'
];

const EVENT_COLORS: Record<string, string> = {
  LOGIN_SUCCESS: '#22c55e',
  LOGIN_FAILED: '#ef4444',
  LOGOUT: '#6b7280',
  PASSWORD_CHANGE: '#f59e0b',
  USER_CREATED: '#3b82f6',
  USER_DELETED: '#ef4444',
  ROLE_CHANGE: '#f59e0b',
  DATA_EXPORT: '#8b5cf6',
  OAUTH_CONNECT: '#3b82f6',
  OAUTH_DISCONNECT: '#f59e0b',
  TOKEN_REFRESH: '#6b7280',
  UPLOAD: '#3b82f6',
  SETTINGS_CHANGE: '#f59e0b',
};

const EVENT_ICONS: Record<string, string> = {
  LOGIN_SUCCESS: '✅',
  LOGIN_FAILED: '❌',
  LOGOUT: '🚪',
  PASSWORD_CHANGE: '🔑',
  USER_CREATED: '👤',
  USER_DELETED: '🗑️',
  ROLE_CHANGE: '🛡️',
  DATA_EXPORT: '📥',
  OAUTH_CONNECT: '🔗',
  OAUTH_DISCONNECT: '🔓',
  TOKEN_REFRESH: '🔄',
  UPLOAD: '📤',
  SETTINGS_CHANGE: '⚙️',
};

export default function AuditLog() {
  const { user } = useAuth();
  const [logs, setLogs] = useState<AuditEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [perPage] = useState(50);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [eventType, setEventType] = useState('');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [ipFilter, setIpFilter] = useState('');

  // Expanded row
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const isSuperAdmin = user?.role === 'superadmin' || user?.is_superuser;

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.append('page', String(page));
      params.append('per_page', String(perPage));
      if (eventType) params.append('event_type', eventType);
      if (search) params.append('search', search);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (ipFilter) params.append('ip_address', ipFilter);

      const token = localStorage.getItem('token');
      const res = await fetch(`/api/v1/audit-logs?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.status === 403) {
        setError('Access denied. Superadmin role required.');
        return;
      }
      if (!res.ok) throw new Error('Failed to fetch audit logs');

      const data: AuditResponse = await res.json();
      setLogs(data.logs);
      setTotal(data.total);
      setPages(data.pages);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [page, perPage, eventType, search, dateFrom, dateTo, ipFilter]);

  useEffect(() => {
    if (isSuperAdmin) fetchLogs();
  }, [fetchLogs, isSuperAdmin]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchLogs();
  };

  const clearFilters = () => {
    setEventType('');
    setSearch('');
    setDateFrom('');
    setDateTo('');
    setIpFilter('');
    setPage(1);
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleString('en-ZA', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit'
    });
  };

  if (!isSuperAdmin) {
    return (
      <div className="audit-denied">
        <h2>🔒 Access Denied</h2>
        <p>Audit logs are only available to superadmin users.</p>
      </div>
    );
  }

  return (
    <div className="audit-container">
      <div className="audit-header">
        <h1>🔒 Audit Log</h1>
        <span className="audit-total">{total} events</span>
      </div>

      <form className="audit-filters" onSubmit={handleSearch}>
        <div className="audit-filter-row">
          <select value={eventType} onChange={e => setEventType(e.target.value)}>
            <option value="">All Events</option>
            {EVENT_TYPES.map(t => (
              <option key={t} value={t}>{EVENT_ICONS[t] || '📋'} {t.replace(/_/g, ' ')}</option>
            ))}
          </select>
          <input
            type="text"
            placeholder="Search details..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <input
            type="text"
            placeholder="IP Address"
            value={ipFilter}
            onChange={e => setIpFilter(e.target.value)}
          />
        </div>
        <div className="audit-filter-row">
          <label>
            From:
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
          </label>
          <label>
            To:
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} />
          </label>
          <button type="submit" className="btn-filter">🔍 Filter</button>
          <button type="button" className="btn-clear" onClick={clearFilters}>✕ Clear</button>
        </div>
      </form>

      {error && <div className="audit-error">{error}</div>}

      {loading ? (
        <div className="audit-loading">Loading audit logs...</div>
      ) : (
        <>
          <div className="audit-table-wrapper">
            <table className="audit-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Event</th>
                  <th>User</th>
                  <th>IP Address</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 ? (
                  <tr><td colSpan={5} className="audit-empty">No audit logs found</td></tr>
                ) : logs.map(log => (
                  <React.Fragment key={log.id}>
                    <tr
                      className={`audit-row ${expandedId === log.id ? 'expanded' : ''}`}
                      onClick={() => setExpandedId(expandedId === log.id ? null : log.id)}
                    >
                      <td className="audit-time">{formatDate(log.created_at)}</td>
                      <td>
                        <span
                          className="audit-badge"
                          style={{ backgroundColor: EVENT_COLORS[log.event_type] || '#6b7280' }}
                        >
                          {EVENT_ICONS[log.event_type] || '📋'} {log.event_type.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td>{log.user_email || (log.user_id ? `User #${log.user_id}` : '—')}</td>
                      <td className="audit-ip">{log.ip_address || '—'}</td>
                      <td className="audit-preview">
                        {log.details ? JSON.stringify(log.details).substring(0, 60) + '...' : '—'}
                      </td>
                    </tr>
                    {expandedId === log.id && (
                      <tr className="audit-detail-row">
                        <td colSpan={5}>
                          <div className="audit-detail">
                            <div className="audit-detail-grid">
                              <div><strong>Event ID:</strong> {log.id}</div>
                              <div><strong>Resource:</strong> {log.resource_type ? `${log.resource_type} #${log.resource_id}` : '—'}</div>
                              <div><strong>User Agent:</strong> <span className="audit-ua">{log.user_agent || '—'}</span></div>
                            </div>
                            {log.details && (
                              <div className="audit-detail-json">
                                <strong>Full Details:</strong>
                                <pre>{JSON.stringify(log.details, null, 2)}</pre>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {pages > 1 && (
            <div className="audit-pagination">
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
              <span>Page {page} of {pages}</span>
              <button disabled={page >= pages} onClick={() => setPage(p => p + 1)}>Next →</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
